/**
 * ChangeSummaryBadges
 *
 * Displays diff summary counts as colored inline badges.
 * Shows added (green), removed (red), modified (blue), moved (purple).
 */

import React from 'react';
import type { TaxonomyDiffSummary } from '../types/taxonomy';

interface ChangeSummaryBadgesProps {
  summary: TaxonomyDiffSummary;
  level?: 'labels' | 'all';
  className?: string;
}

interface BadgeItem {
  label: string;
  count: number;
  color: string;
}

const ChangeSummaryBadges: React.FC<ChangeSummaryBadgesProps> = ({
  summary,
  level = 'all',
  className = '',
}) => {
  const badges: BadgeItem[] = [];

  if (level === 'all') {
    if (summary.dimensions_added > 0)
      badges.push({ label: 'dimensions added', count: summary.dimensions_added, color: 'bg-green-100 text-green-700' });
    if (summary.dimensions_removed > 0)
      badges.push({ label: 'dimensions removed', count: summary.dimensions_removed, color: 'bg-red-100 text-red-700' });
    if (summary.dimensions_modified > 0)
      badges.push({ label: 'dimensions modified', count: summary.dimensions_modified, color: 'bg-blue-100 text-blue-700' });
    if (summary.categories_added > 0)
      badges.push({ label: 'categories added', count: summary.categories_added, color: 'bg-green-100 text-green-700' });
    if (summary.categories_removed > 0)
      badges.push({ label: 'categories removed', count: summary.categories_removed, color: 'bg-red-100 text-red-700' });
    if (summary.categories_modified > 0)
      badges.push({ label: 'categories modified', count: summary.categories_modified, color: 'bg-blue-100 text-blue-700' });
  }

  if (summary.labels_added > 0)
    badges.push({ label: 'added', count: summary.labels_added, color: 'bg-green-100 text-green-700' });
  if (summary.labels_removed > 0)
    badges.push({ label: 'removed', count: summary.labels_removed, color: 'bg-red-100 text-red-700' });
  if (summary.labels_modified > 0)
    badges.push({ label: 'modified', count: summary.labels_modified, color: 'bg-blue-100 text-blue-700' });
  if (summary.labels_moved > 0)
    badges.push({ label: 'moved', count: summary.labels_moved, color: 'bg-purple-100 text-purple-700' });

  if (badges.length === 0) {
    return <span className="text-xs text-gray-400">No changes</span>;
  }

  return (
    <div className={`flex flex-wrap gap-1.5 ${className}`}>
      {badges.map((badge) => (
        <span
          key={badge.label}
          className={`inline-flex items-center text-xs font-medium px-1.5 py-0.5 rounded ${badge.color}`}
        >
          {badge.count} {badge.label}
        </span>
      ))}
    </div>
  );
};

export default ChangeSummaryBadges;
